import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { analyticsAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import {
  Users,
  FileText,
  DollarSign,
  TrendingUp,
  Loader2,
  GraduationCap,
  Briefcase,
  Globe,
  ArrowLeft,
  Activity,
  Target,
  PieChart,
  BarChart3,
  Calendar,
  UserPlus,
  CheckCircle2,
  Clock,
  XCircle,
  Award
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';

const COLORS = ['#eab308', '#22c55e', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
const STATUS_COLORS = {
  enrolled: '#22c55e',
  accepted: '#3b82f6',
  under_review: '#eab308',
  submitted: '#f97316',
  rejected: '#ef4444',
  draft: '#6b7280'
};

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [applicationsByStatus, setApplicationsByStatus] = useState([]);
  const [applicationsByCountry, setApplicationsByCountry] = useState([]);
  const [applicationsByMonth, setApplicationsByMonth] = useState([]);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [conversionRates, setConversionRates] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        statusRes,
        countryRes,
        monthRes,
        agentRes,
        activityRes,
        conversionRes
      ] = await Promise.all([
        analyticsAPI.getOverview(),
        analyticsAPI.getApplicationsByStatus(),
        analyticsAPI.getApplicationsByCountry(),
        analyticsAPI.getApplicationsByMonth(),
        analyticsAPI.getAgentPerformance(),
        analyticsAPI.getRecentActivity(15),
        analyticsAPI.getConversionRates()
      ]);

      setOverview(overviewRes.data);
      
      // Transform applications by status to include labels
      const statusLabels = {
        draft: 'Draft',
        submitted: 'Submitted',
        under_review: 'Under Review',
        document_requested: 'Docs Requested',
        passed_to_counselor: 'With Counselor',
        passed_to_app_team: 'With App Team',
        accepted: 'Accepted',
        rejected: 'Rejected',
        enrolled: 'Enrolled'
      };
      const transformedStatus = (statusRes.data || []).map(s => ({
        ...s,
        label: statusLabels[s.status] || s.status
      }));
      setApplicationsByStatus(transformedStatus);
      
      setApplicationsByCountry(countryRes.data || []);
      
      // Transform applications by month to include labels
      const transformedMonth = (monthRes.data || []).map(m => ({
        ...m,
        label: m.month ? new Date(m.month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Unknown'
      }));
      setApplicationsByMonth(transformedMonth);
      
      setAgentPerformance(agentRes.data || []);
      setRecentActivity(activityRes.data || []);
      setConversionRates(conversionRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'application':
        return <FileText className="w-4 h-4 text-blue-600" />;
      case 'student':
        return <UserPlus className="w-4 h-4 text-green-600" />;
      case 'agent':
        return <Briefcase className="w-4 h-4 text-teal-600" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const handleActivityClick = (activity) => {
    if (!activity?.type) return;
    switch (activity.type) {
      case 'application':
        navigate(`/admin?tab=applications${activity.reference_id ? `&id=${activity.reference_id}` : ''}`);
        break;
      case 'agent':
        navigate(`/admin?tab=agents${activity.reference_id ? `&id=${activity.reference_id}` : ''}`);
        break;
      case 'student':
        navigate(`/admin?tab=students${activity.reference_id ? `&id=${activity.reference_id}` : ''}`);
        break;
      default:
        navigate('/admin');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
      </div>
    );
  }

  // Calculate pending from status data
  const pendingCount = applicationsByStatus
    .filter(s => ['submitted', 'under_review', 'document_requested', 'passed_to_counselor', 'passed_to_app_team'].includes(s.status))
    .reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="min-h-screen bg-[#f8f9fa] overflow-x-hidden">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-gray-500 hover:text-gray-800 dark:text-white"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-800 flex items-center gap-2 sm:gap-3">
                <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-teal-600" />
                Analytics Dashboard
              </h1>
              <p className="text-gray-500 text-sm mt-1">Platform performance and insights</p>
            </div>
          </div>
          <Button
            onClick={loadAnalytics}
            variant="outline"
            className="border-gray-200 text-gray-600 hover:bg-[#e9ecef] w-full sm:w-auto"
            data-testid="refresh-analytics-btn"
          >
            <Activity className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 mb-8" data-testid="overview-stats">
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <span className="text-gray-500 text-xs sm:text-sm">Students</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-white">{overview?.total_students || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="p-1.5 sm:p-2 bg-teal-100 rounded-lg">
                <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
              </div>
              <span className="text-gray-500 text-xs sm:text-sm">Agents</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-white">{overview?.total_agents || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
              <span className="text-gray-500 text-xs sm:text-sm">Applications</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-white">{overview?.total_applications || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
                <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
              <span className="text-gray-500 text-xs sm:text-sm">Courses</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-white">{overview?.total_courses || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100 col-span-2 sm:col-span-1">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              </div>
              <span className="text-gray-500 text-xs sm:text-sm">Est. Revenue</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-white">{formatCurrency(overview?.estimated_revenue)}</p>
          </div>
        </div>

        {/* Application Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
          <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 rounded-xl p-3 sm:p-4 border border-green-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-xs sm:text-sm font-medium">Accepted</p>
                <p className="text-2xl sm:text-3xl font-semibold text-gray-800 mt-1">{overview?.accepted_applications || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-green-500/50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-xl p-3 sm:p-4 border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-xs sm:text-sm font-medium">Total Offers</p>
                <p className="text-2xl sm:text-3xl font-semibold text-gray-800 mt-1">{overview?.total_offers || 0}</p>
              </div>
              <Award className="w-8 h-8 sm:w-10 sm:h-10 text-blue-500/50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 rounded-xl p-3 sm:p-4 border border-yellow-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-600 text-xs sm:text-sm font-medium">Pending</p>
                <p className="text-2xl sm:text-3xl font-semibold text-gray-800 mt-1">{overview?.pending_applications || pendingCount}</p>
              </div>
              <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-teal-600/50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 rounded-xl p-3 sm:p-4 border border-purple-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-xs sm:text-sm font-medium">Acceptance Rate</p>
                <p className="text-2xl sm:text-3xl font-semibold text-gray-800 mt-1">{conversionRates?.acceptance_rate || 0}%</p>
              </div>
              <Target className="w-8 h-8 sm:w-10 sm:h-10 text-purple-500/50" />
            </div>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
          {/* Applications by Month Chart */}
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 overflow-hidden" data-testid="applications-by-month-chart">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-teal-600 flex-shrink-0" />
              Applications Over Time
            </h3>
            <div className="h-[250px] sm:h-[300px]">
              {applicationsByMonth.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={applicationsByMonth}>
                    <defs>
                      <linearGradient id="colorApps" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#eab308" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="label" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#eab308"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorApps)"
                      name="Applications"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">No data available</div>
              )}
            </div>
          </div>

          {/* Applications by Status Pie Chart */}
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 overflow-hidden" data-testid="applications-by-status-chart">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-teal-600 flex-shrink-0" />
              Applications by Status
            </h3>
            <div className="h-[250px] sm:h-[300px]">
              {applicationsByStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={applicationsByStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={3}
                      dataKey="count"
                      nameKey="label"
                      label={({ label, percent }) => `${label} ${(percent * 100).toFixed(0)}%`}
                    >
                      {applicationsByStatus.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">No data available</div>
              )}
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
          {/* Applications by Country */}
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 overflow-hidden" data-testid="applications-by-country-chart">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-teal-600 flex-shrink-0" />
              Top Destinations
            </h3>
            <div className="h-[250px] sm:h-[300px]">
              {applicationsByCountry.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={applicationsByCountry} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis type="number" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <YAxis dataKey="country" type="category" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 10 }} width={60} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                    />
                    <Bar dataKey="count" fill="#eab308" radius={[0, 4, 4, 0]} name="Applications" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">No data available</div>
              )}
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 overflow-hidden" data-testid="conversion-funnel">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-teal-600 flex-shrink-0" />
              Conversion Metrics
            </h3>
            <div className="space-y-6">
              {/* Application Pipeline */}
              <div>
                <p className="text-gray-500 text-xs sm:text-sm mb-4">Application Pipeline</p>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-xs sm:text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-300">Total Applications</span>
                      <span className="text-gray-800 font-medium">{conversionRates?.total_applications || 0}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div 
                        className="bg-gradient-to-r from-yellow-500 to-yellow-400 h-2 sm:h-3 rounded-full"
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs sm:text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-300">Accepted</span>
                      <span className="text-gray-800 font-medium">{conversionRates?.accepted || 0} ({conversionRates?.acceptance_rate || 0}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-green-400 h-2 sm:h-3 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(conversionRates?.acceptance_rate || 0, 100)}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs sm:text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-300">Enrolled</span>
                      <span className="text-gray-800 font-medium">{conversionRates?.enrolled || 0} ({conversionRates?.enrollment_rate || 0}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-blue-400 h-2 sm:h-3 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(conversionRates?.enrollment_rate || 0, 100)}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs sm:text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-300">Rejected</span>
                      <span className="text-gray-800 font-medium">{conversionRates?.rejected || 0}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div 
                        className="bg-gradient-to-r from-red-500 to-red-400 h-2 sm:h-3 rounded-full transition-all duration-500"
                        style={{ width: `${conversionRates?.total_applications > 0 ? (conversionRates.rejected / conversionRates.total_applications * 100) : 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row - Agent Performance & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Agents */}
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 overflow-hidden" data-testid="agent-performance">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-teal-600 flex-shrink-0" />
              <span className="truncate">Top Performing Agents</span>
            </h3>
            {agentPerformance.length > 0 ? (
              <div className="space-y-3">
                {agentPerformance.slice(0, 5).map((agent, index) => (
                  <div 
                    key={agent.id} 
                    className="flex items-center justify-between p-2 sm:p-3 bg-[#e9ecef]/50 rounded-lg gap-2"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        index === 0 ? 'bg-teal-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'
                      } text-black font-semibold text-xs sm:text-sm`}>
                        {index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-gray-800 font-medium text-sm sm:text-base truncate">{agent.name || agent.full_name}</p>
                        <p className="text-gray-500 text-xs truncate">{agent.company || agent.company_name}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-teal-600 font-medium text-sm sm:text-base">{formatCurrency(agent.total_commission)}</p>
                      <p className="text-gray-500 text-xs">{agent.total_referrals} refs</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No agent performance data yet</p>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 overflow-hidden" data-testid="recent-activity">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-teal-600 flex-shrink-0" />
              Recent Activity
            </h3>
            {recentActivity.length > 0 ? (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {recentActivity.map((activity, index) => (
                  <button
                    type="button"
                    key={index}
                    onClick={() => handleActivityClick(activity)}
                    className="w-full text-left flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-[#e9ecef]/50 rounded-lg hover:bg-teal-50 hover:ring-1 hover:ring-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all cursor-pointer group"
                    data-testid={`recent-activity-item-${index}`}
                  >
                    <div className="p-1.5 sm:p-2 bg-gray-200 rounded-lg mt-0.5 flex-shrink-0 group-hover:bg-white">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 text-xs sm:text-sm font-medium truncate group-hover:text-teal-700">{activity.title}</p>
                      <p className="text-gray-500 text-xs truncate">{activity.description}</p>
                    </div>
                    <span className="text-gray-500 text-xs whitespace-nowrap flex-shrink-0">
                      {formatTimestamp(activity.timestamp)}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
